import { isAxiosError } from "axios";

import instance, { api } from "./instance";
import { REQUEST_STATUSES } from "../constants";
import { getErrorResponse } from "../utils/helper";

interface MediaShare {
  id: number;
  title: string;
  url: string;
  contactId: number;
  propertyId: number;
  email: string;
  phone: string;
  message: string;
  createdById: number;
  type: string;
  hasOpened: boolean;
  options: { scope: string };
  processed: boolean;
  createdAt: string;
  updatedAt: string;
  activity: string;
}

export class MediaShareService {
  static async getMediaShares(propertyId: number, query: { offset: number; limit: number }) {
    try {
      const resp = await api<{
        count: number;
        data: MediaShare[];
      }>({
        method: "get",
        url: `/api/v2/media-shares/${propertyId}`,
        params: query,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: { data: [], count: 0 },
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async getMediaShare(propertyId: number, mediaShareId: number) {
    try {
      const resp = await api<MediaShare>({
        method: "get",
        url: `/api/v2/media-shares/${propertyId}/${mediaShareId}`,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async addMediaShare(propertyId: number, data: Partial<MediaShare>) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "post",
        url: `/api/v2/media-shares/${propertyId}`,
        data,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async addPublicMediaShare(propertyId: number, data: Partial<MediaShare>) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "post",
        url: `/api/v2/public/media-shares/${propertyId}`,
        data,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async updateMediaShare(propertyId: number, mediaShareId: number, data: Partial<MediaShare>) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "put",
        url: `/api/v2/media-shares/${propertyId}/${mediaShareId}`,
        data,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async updatePublicMediaShare(propertyId: number, mediaShareId: number, data: Partial<MediaShare>) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "put",
        url: `/api/v2/public/media-shares/${propertyId}/${mediaShareId}`,
        data,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async deleteMediaShare(propertyId: number, mediaShareId: number) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "delete",
        url: `/api/v2/media-shares/${propertyId}/${mediaShareId}`,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async getPublicMediaShare(propertyId: number, mediaShareId: number) {
    try {
      const resp = await instance({
        method: "get",
        url: `/api/v2/public/media-shares/${propertyId}/${mediaShareId}`,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async sendMediaShare(propertyId: number, mediaShareId: number) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "post",
        url: `/api/v2/media-shares/${propertyId}/${mediaShareId}/send`,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async sendPublicMediaShare(propertyId: number, mediaShareId: number) {
    try {
      const resp = await api<{
        message: string;
      }>({
        method: "post",
        url: `/api/v2/public/media-shares/${propertyId}/${mediaShareId}/send`,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: isAxiosError(err) ? getErrorResponse(err) : err,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }
}
